import { Card, Empty, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FileText, HelpCircle, History, Layers } from "lucide-react";
import React from "react";
import type { SchemaRunStatus } from "@/hooks/useSchemaManagement";
import { SchemaRunProgress } from "./SchemaRunProgress";

const { Text } = Typography;

interface RecentRunsListProps {
	runs: SchemaRunStatus[];
}

export function RecentRunsList({ runs }: RecentRunsListProps) {
	const getTypeIcon = (type: string) => {
		switch (type) {
			case "article":
				return <FileText size={14} />;
			case "faq":
				return <HelpCircle size={14} />;
			case "full":
				return <Layers size={14} />;
			default:
				return null;
		}
	};

	const getTypeLabel = (type: string) => {
		switch (type) {
			case "article":
				return "Article Schema";
			case "faq":
				return "FAQ Schema";
			case "full":
				return "Full Implementation";
			default:
				return type;
		}
	};

	const formatDuration = (started: string, completed?: string) => {
		if (!completed) return "-";

		const start = new Date(started).getTime();
		const end = new Date(completed).getTime();
		const durationMs = end - start;

		const seconds = Math.floor(durationMs / 1000);
		const minutes = Math.floor(seconds / 60);

		if (minutes > 0) {
			const remainingSeconds = seconds % 60;
			return `${minutes}m ${remainingSeconds}s`;
		}

		return `${seconds}s`;
	};

	const formatTimeAgo = (timestamp: string) => {
		const now = Date.now();
		const time = new Date(timestamp).getTime();
		const diffMs = now - time;

		const minutes = Math.floor(diffMs / (1000 * 60));
		const hours = Math.floor(diffMs / (1000 * 60 * 60));
		const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (days > 0) return `${days}d ago`;
		if (hours > 0) return `${hours}h ago`;
		if (minutes > 0) return `${minutes}m ago`;
		return "Just now";
	};

	const columns: ColumnsType<SchemaRunStatus> = [
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 200,
			render: (type: string) => (
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					{getTypeIcon(type)}
					<span>{getTypeLabel(type)}</span>
				</div>
			),
		},
		{
			title: "Progress",
			key: "progress",
			render: (_, run) => <SchemaRunProgress run={run} />,
		},
		{
			title: "Started",
			dataIndex: "started_at",
			key: "started_at",
			width: 120,
			render: (timestamp: string) => (
				<Text type="secondary">{formatTimeAgo(timestamp)}</Text>
			),
		},
		{
			title: "Duration",
			key: "duration",
			width: 100,
			render: (_, run) => (
				<Text type="secondary">
					{formatDuration(run.started_at, run.completed_at)}
				</Text>
			),
		},
	];

	if (runs.length === 0) {
		return (
			<Card
				title={
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<History size={20} />
						<span>Recent Runs</span>
					</div>
				}
			>
				<Empty
					description="No schema implementation runs yet"
					style={{ padding: "40px 0" }}
				/>
			</Card>
		);
	}

	return (
		<Card
			title={
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<History size={20} />
					<span>Recent Runs</span>
				</div>
			}
		>
			<Table<SchemaRunStatus>
				columns={columns}
				dataSource={runs}
				rowKey="run_id"
				pagination={false}
				size="middle"
			/>
		</Card>
	);
}
